var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { ArrayInput, BooleanInput, DateInput, DateTimeInput, NumberInput, ReferenceArrayInput, ReferenceInput, SelectArrayInput, SelectInput, SimpleFormIterator, TextInput, getFieldLabelTranslationArgs, required, useCheckMinimumRequiredProps, useTranslate, } from 'react-admin';
import { useForm } from 'react-final-form';
import Introspecter from './Introspecter';
export const IntrospectedInputGuesser = (_a) => {
    var { fields, readableFields, writableFields, schema, schemaAnalyzer, validate } = _a, props = __rest(_a, ["fields", "readableFields", "writableFields", "schema", "schemaAnalyzer", "validate"]);
    const translate = useTranslate();
    const form = useForm();
    // Pause the validation while the Final Form field is registered to prevent a form state desynchronization bug when using async validators.
    // Since the field is not registered directly because of the introspection, Final Form is using the previous form state (without the field) when notifying after the async validation done during the registration.
    // See also https://github.com/final-form/react-final-form/issues/780.
    form.pauseValidation();
    useEffect(() => {
        form.resumeValidation();
    });
    const field = fields.find(({ name }) => name === props.source);
    if (!field) {
        // eslint-disable-next-line no-console
        console.error(`Field ${props.source} not present inside API description for the resource ${props.resource}`);
        return null;
    }
    const guessedValidate = !validate && field.required ? [required()] : validate;
    if (field.reference !== null && typeof field.reference === 'object') {
        if (field.maxCardinality === 1) {
            return (React.createElement(ReferenceInput, Object.assign({ key: field.name, validate: guessedValidate }, props, { reference: field.reference.name, source: field.name, allowEmpty: true }),
                React.createElement(SelectInput, { optionText: schemaAnalyzer.getFieldNameFromSchema(field.reference) })));
        }
        // Not needed after React-Admin >= 3.10.4 (see https://github.com/marmelab/react-admin/pull/5606).
        const translatedLabel = translate(...getFieldLabelTranslationArgs({
            resource: props.resource,
            source: field.name,
        }));
        return (React.createElement(ReferenceArrayInput, Object.assign({ key: field.name, label: translatedLabel, validate: guessedValidate }, props, { reference: field.reference.name, source: field.name, allowEmpty: true }),
            React.createElement(SelectArrayInput, { optionText: schemaAnalyzer.getFieldNameFromSchema(field.reference) })));
    }
    let { format, parse } = props;
    const fieldType = schemaAnalyzer.getFieldType(field);
    if (['integer_id', 'id'].includes(fieldType) || field.name === 'id') {
        const prefix = `/${props.resource}/`;
        format = (value) => {
            if (typeof value === 'string' && value.indexOf(prefix) === 0) {
                const id = value.substring(prefix.length);
                if (['integer_id', 'integer'].includes(fieldType)) {
                    return parseInt(id, 10);
                }
                return id;
            }
            return value;
        };
    }
    const formatEmbedded = (value) => JSON.stringify(value);
    const parseEmbedded = (value) => JSON.parse(value);
    if (field.embedded !== null && field.maxCardinality === 1) {
        format = formatEmbedded;
        parse = parseEmbedded;
    }
    let textInputFormat = (value) => value;
    let textInputParse = (value) => value;
    switch (fieldType) {
        case 'array':
            if (field.embedded !== null && field.maxCardinality !== 1) {
                textInputFormat = formatEmbedded;
                textInputParse = parseEmbedded;
            }
            return (React.createElement(ArrayInput, Object.assign({ key: field.name, validate: guessedValidate }, props, { source: field.name }),
                React.createElement(SimpleFormIterator, null,
                    React.createElement(TextInput, { source: "", format: textInputFormat, parse: textInputParse }))));
        case 'integer':
        case 'integer_id':
            return (React.createElement(NumberInput, Object.assign({ key: field.name, validate: guessedValidate }, props, { format: format, parse: parse, source: field.name })));
        case 'float':
            return (React.createElement(NumberInput, Object.assign({ key: field.name, step: "0.1", validate: guessedValidate }, props, { format: format, parse: parse, source: field.name })));
        case 'boolean':
            return (React.createElement(BooleanInput, Object.assign({ key: field.name, validate: guessedValidate }, props, { format: format, parse: parse, source: field.name })));
        case 'date':
            return (React.createElement(DateInput, Object.assign({ key: field.name, validate: guessedValidate }, props, { format: format, parse: parse, source: field.name })));
        case 'dateTime':
            return (React.createElement(DateTimeInput, Object.assign({ key: field.name, validate: guessedValidate }, props, { format: format, parse: parse, source: field.name })));
        default:
            return (React.createElement(TextInput, Object.assign({ key: field.name, validate: guessedValidate }, props, { format: format, parse: parse, source: field.name })));
    }
};
const InputGuesser = (props) => {
    useCheckMinimumRequiredProps('InputGuesser', ['resource'], props);
    const { resource } = props, rest = __rest(props, ["resource"]);
    if (!resource) {
        return null;
    }
    return (React.createElement(Introspecter, Object.assign({ component: IntrospectedInputGuesser, resource: resource, includeDeprecated: true }, rest)));
};
InputGuesser.propTypes = {
    source: PropTypes.string.isRequired,
    alwaysOn: PropTypes.bool,
};
export default InputGuesser;
//# sourceMappingURL=InputGuesser.js.map